import { useCallback, useEffect, useState } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  CircularProgress,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { CustomButton } from "@/components/common/customButton";
import { ErrorDialog } from "@/components/common/errorDialog";
import { useErrorDialog } from "@/hooks/useErrorDialog";
import {
  deleteReport,
  getCoverageEvidence,
  getReport,
  getReportCoverage,
  getReportMetrics,
} from "@/services/marketReportApi/marketReportApi";
import type {
  CoverageSlice,
  MarketMetric,
  MarketReportDetail,
  SourceEvidence,
} from "@/types/marketReport";
import { renderSubTitle } from "@/components/renderComponents";
import { ReportStatusBadge } from "./components/ReportStatusBadge";
import { formatDate } from "@/utils/formatDate";
import "@/assets/css/Text-General.css";
import "@/assets/css/marketReports.css";

const textGeneralAutoHeight = {
  "&&": { height: "auto !important" },
  mb: 0,
  pb: 0,
  lineHeight: 1.4,
} as const;

function CoverageSlicePanel({ slice }: { slice: CoverageSlice }) {
  const [expanded, setExpanded] = useState(false);
  const [evidence, setEvidence] = useState<SourceEvidence[]>([]);
  const [loadingEvidence, setLoadingEvidence] = useState(false);

  const loadEvidence = async () => {
    if (evidence.length > 0) {
      setExpanded((prev) => !prev);
      return;
    }
    setLoadingEvidence(true);
    const response = await getCoverageEvidence(slice.id);
    setLoadingEvidence(false);
    if (response.success && response.data) {
      setEvidence(response.data);
      setExpanded(true);
    }
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={() => void loadEvidence()}
      className="market-reports-slice-card"
      disableGutters
      elevation={0}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0 }}>
          <Typography
            className="text-general-page-subheader"
            sx={{ ...textGeneralAutoHeight, wordBreak: "break-word" }}
          >
            {slice.geography} / {slice.sector}
            {slice.topic ? ` — ${slice.topic}` : ""}
          </Typography>
          <Typography
            className="text-general-page-text"
            sx={{
              ...textGeneralAutoHeight,
              whiteSpace: "normal",
              wordBreak: "break-word",
            }}
          >
            {slice.summary}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        {loadingEvidence ? (
          <CircularProgress size={20} />
        ) : evidence.length === 0 ? (
          <Typography className="text-general-page-text" sx={textGeneralAutoHeight}>
            No evidence loaded.
          </Typography>
        ) : (
          evidence.map((item) => (
            <div key={item.id} className="market-reports-evidence">
              <Typography
                className="text-general-page-subheader"
                sx={textGeneralAutoHeight}
              >
                {item.citation_label}
              </Typography>
              <Typography
                className="text-general-page-text"
                sx={{
                  ...textGeneralAutoHeight,
                  mt: 0.5,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {item.source_text}
              </Typography>
            </div>
          ))
        )}
      </AccordionDetails>
    </Accordion>
  );
}

const ReportDetail = () => {
  const { reportId } = useParams<{ reportId: string }>();
  const navigate = useNavigate();
  const [report, setReport] = useState<MarketReportDetail | null>(null);
  const [coverage, setCoverage] = useState<CoverageSlice[]>([]);
  const [metrics, setMetrics] = useState<MarketMetric[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const { errorDialog, showError, hideError } = useErrorDialog();

  const loadDetail = useCallback(async () => {
    if (!reportId) return;
    setLoading(true);

    const [reportRes, coverageRes, metricsRes] = await Promise.all([
      getReport(reportId),
      getReportCoverage(reportId),
      getReportMetrics(reportId),
    ]);

    setLoading(false);

    if (reportRes.success && reportRes.data) {
      setReport(reportRes.data);
    } else {
      showError(
        undefined,
        typeof reportRes.message === "string"
          ? reportRes.message
          : "Report not found",
      );
      return;
    }

    if (coverageRes.success && coverageRes.data) {
      setCoverage(coverageRes.data);
    }
    if (metricsRes.success && metricsRes.data) {
      setMetrics(metricsRes.data);
    }
  }, [reportId, showError]);

  useEffect(() => {
    void loadDetail();
  }, [loadDetail]);

  const handleDelete = async () => {
    if (!reportId) return;
    const confirmed = window.confirm(
      "Delete this report and all extracted data? This cannot be undone.",
    );
    if (!confirmed) return;

    setDeleting(true);
    const response = await deleteReport(reportId);
    setDeleting(false);

    if (response.success) {
      navigate("/market-reports");
    } else {
      showError(
        undefined,
        typeof response.message === "string"
          ? response.message
          : "Failed to delete report",
      );
    }
  };

  if (loading) {
    return (
      <Box
        className="market-reports-detail-page"
        sx={{ display: "flex", justifyContent: "center", py: 6 }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!report) {
    return (
      <Box className="market-reports-detail-page">
        <Alert severity="error">Report not found.</Alert>
        <Link
          component={RouterLink}
          to="/market-reports"
          underline="hover"
          sx={{
            mt: 2,
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            "& .MuiSvgIcon-root": { color: "#0E4664" },
          }}
        >
          <ArrowBackIcon fontSize="small" />
          Back to reports
        </Link>
      </Box>
    );
  }

  const title =
    report.title || report.original_filename || report.id;

  return (
    <Box className="market-reports-detail-page">
      <Paper elevation={0} className="market-reports-detail-card" sx={{ p: 2.5 }}>
        <Link
          component={RouterLink}
          to="/market-reports"
          underline="hover"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            mb: 2,
            "& .MuiSvgIcon-root": { color: "#0E4664" },
          }}
        >
          <ArrowBackIcon fontSize="small" />
          Back to reports
        </Link>

        <div className="market-reports-detail-header">
          <Box>
            <Typography
              className="text-general-page-header"
              sx={{
                ...textGeneralAutoHeight,
                fontWeight: "500 !important",
                wordBreak: "break-word",
                mb: 1.5,
              }}
            >
              {title}
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <Typography className="text-general-page-text" sx={textGeneralAutoHeight}>
                <Box component="span" sx={{ fontWeight: 600 }}>
                  Publisher:
                </Box>{" "}
                {report.publisher ?? "—"}
              </Typography>
              <Typography className="text-general-page-text" sx={textGeneralAutoHeight}>
                <Box component="span" sx={{ fontWeight: 600 }}>
                  Published:
                </Box>{" "}
                {formatDate(report.publication_date)}
              </Typography>
              <Typography className="text-general-page-text" sx={textGeneralAutoHeight}>
                <Box component="span" sx={{ fontWeight: 600 }}>
                  Uploaded:
                </Box>{" "}
                {formatDate(report.created_at)}
              </Typography>
              <ReportStatusBadge status={report.status} />
            </Box>
          </Box>
          <CustomButton
            text={deleting ? "Deleting…" : "Delete report"}
            handleButtonPress={() => void handleDelete()}
            isButtonDisabled={deleting}
            isLoading={deleting}
            isDeleteButton
          />
        </div>

        {report.status === "failed" && report.error_message ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {report.error_message}
          </Alert>
        ) : null}

        <Box sx={{ mt: 2 }}>
          {renderSubTitle(`Coverage (${coverage.length})`)}
          <Box sx={{ mt: 2, mb: 3 }}>
            {coverage.length === 0 ? (
              <Typography className="text-general-page-text" sx={textGeneralAutoHeight}>
                No coverage slices extracted yet.
              </Typography>
            ) : (
              <Box>
                {coverage.map((slice) => (
                  <CoverageSlicePanel key={slice.id} slice={slice} />
                ))}
              </Box>
            )}
          </Box>
        </Box>

        <Box>
          {renderSubTitle(`Metrics (${metrics.length})`)}
          <Box sx={{ mt: 2 }}>
            {metrics.length === 0 ? (
              <Typography className="text-general-page-text" sx={textGeneralAutoHeight}>
                No metrics extracted yet.
              </Typography>
            ) : (
              <TableContainer
                component={Paper}
                variant="outlined"
                className="market-reports-metrics-table"
              >
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <Typography
                          className="text-general-page-subheader"
                          sx={textGeneralAutoHeight}
                        >
                          Metric
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          className="text-general-page-subheader"
                          sx={textGeneralAutoHeight}
                        >
                          Value
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          className="text-general-page-subheader"
                          sx={textGeneralAutoHeight}
                        >
                          Unit
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          className="text-general-page-subheader"
                          sx={textGeneralAutoHeight}
                        >
                          Period
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {metrics.map((metric) => (
                      <TableRow key={metric.id}>
                        <TableCell>
                          <Typography
                            className="text-general-page-text"
                            sx={textGeneralAutoHeight}
                          >
                            {metric.metric_name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            className="text-general-page-subheader"
                            sx={textGeneralAutoHeight}
                          >
                            {metric.metric_value}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            className="text-general-page-text"
                            sx={textGeneralAutoHeight}
                          >
                            {metric.metric_unit ?? "—"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            className="text-general-page-text"
                            sx={textGeneralAutoHeight}
                          >
                            {metric.metric_period ?? "—"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Box>
      </Paper>

      <ErrorDialog
        open={errorDialog.open}
        onClose={hideError}
        apiResponse={errorDialog.apiResponse}
        customMessage={errorDialog.customMessage}
        title={errorDialog.title}
      />
    </Box>
  );
};

export default ReportDetail;
